import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { SECTORS, type Translatable } from '@hrlabs/shared-types';
import { sectorPageSaveSchema } from '@hrlabs/validation';
import { getRow, saveRow } from '../../lib/db';
import { useToast } from '../../components/Toast';
import { BilingualField } from '../../components/BilingualField';
import { ArrayField } from '../../components/ArrayField';

type Tr = Translatable;
const empty = (): Tr => ({ en: '', bg: '' });
const tr = (v: unknown): Tr => {
  const o = (v ?? {}) as Partial<Tr>;
  return { en: o.en ?? '', bg: o.bg ?? '' };
};
const list = <T,>(v: unknown, f: (x: Record<string, unknown>) => T): T[] =>
  Array.isArray(v) ? v.map((x) => f((x ?? {}) as Record<string, unknown>)) : [];

interface Form {
  status: 'draft' | 'published';
  h1: Tr;
  metaTitle: Tr;
  metaDescription: Tr;
  short: Tr;
  answer: Tr;
  titles: Tr[];
  filled: Tr[];
  hard: Tr[];
  how: Tr[];
  faq: { q: Tr; a: Tr }[];
}

const BLANK: Form = {
  status: 'draft',
  h1: empty(), metaTitle: empty(), metaDescription: empty(), short: empty(), answer: empty(),
  titles: [], filled: [], hard: [], how: [], faq: [],
};

export function SectorEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const def = SECTORS.find((r) => r.key === id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState<Form>(BLANK);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!id || !def) return;
    getRow('sectorPages', id).then((r) => {
      if (r) {
        setF({
          status: r.status === 'published' ? 'published' : 'draft',
          h1: tr(r.h1), metaTitle: tr(r.metaTitle), metaDescription: tr(r.metaDescription),
          short: tr(r.short), answer: tr(r.answer),
          titles: list(r.titles, (b) => tr(b)),
          filled: list(r.filled, (b) => tr(b)),
          hard: list(r.hard, (b) => tr(b)),
          how: list(r.how, (b) => tr(b)),
          faq: list(r.faq, (b) => ({ q: tr(b.q), a: tr(b.a) })),
        });
      }
      setLoading(false);
    });
  }, [id, def]);

  if (!def) return <Navigate to="/sectors" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      status: f.status,
      h1: f.h1, metaTitle: f.metaTitle, metaDescription: f.metaDescription, short: f.short, answer: f.answer,
      titles: f.titles, filled: f.filled, hard: f.hard, how: f.how,
      faq: f.faq,
    };
    const parsed = sectorPageSaveSchema.safeParse(payload);
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => `${i.path.join('.') || 'form'}: ${i.message}`));
      notify('danger', 'Please fix the highlighted problems.');
      return;
    }
    setErrors([]);
    setSaving(true);
    try {
      await saveRow('sectorPages', def.key, parsed.data);
      notify('success', f.status === 'published' ? 'Saved and published.' : 'Saved as draft.');
      navigate('/sectors');
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;
  const set = <K extends keyof Form>(k: K) => (v: Form[K]) => setF((p) => ({ ...p, [k]: v }));

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 900 }}>
      <p className="mb-1"><Link to="/sectors">← Sector pages</Link></p>
      <h3 className="fw-bold mb-1">{def.name.en}</h3>
      <p className="text-muted small">
        EN <code>/sectors/{def.slug.en}/</code> · BG <code>/sektori/{def.slug.bg}/</code>
      </p>

      {errors.length > 0 && (
        <div className="alert alert-warning">
          <strong>Cannot save:</strong>
          <ul className="mb-0">{errors.map((x) => (<li key={x}>{x}</li>))}</ul>
        </div>
      )}

      <div className="mb-4" style={{ maxWidth: 260 }}>
        <label className="form-label fw-semibold">Status</label>
        <select className="form-select" value={f.status} onChange={(e) => set('status')(e.target.value as Form['status'])}>
          <option value="draft">draft (hidden)</option>
          <option value="published">published (live)</option>
        </select>
        <div className="form-text">Publishing requires title, meta, card line and answer in both languages, at least one job title and at least three “what makes hiring hard” sentences.</div>
      </div>

      <BilingualField label="H1 (page title)" value={f.h1} onChange={set('h1')} rows={1} />
      <BilingualField label="Meta title" value={f.metaTitle} onChange={set('metaTitle')} rows={1} />
      <BilingualField label="Meta description" value={f.metaDescription} onChange={set('metaDescription')} rows={2} />
      <BilingualField label="Card line (one line on the homepage, sectors hub and recruitment page)" value={f.short} onChange={set('short')} rows={2} />
      <BilingualField label="Answer block (40–80 words, answer-first)" value={f.answer} onChange={set('answer')} rows={4} />

      <ArrayField label="Roles we recruit for (job titles)" items={f.titles} onChange={set('titles')} blank={empty} addLabel="Add job title"
        render={(w, s) => <BilingualField label="Job title" value={w} onChange={s} rows={1} />} />

      <ArrayField label="Roles we have filled (IT page only; leave empty elsewhere)" items={f.filled} onChange={set('filled')} blank={empty} addLabel="Add filled role"
        render={(w, s) => <BilingualField label="Role" value={w} onChange={s} rows={1} />} />

      <ArrayField label="What makes hiring hard in this sector (3–5 specific sentences; must differ from every other sector page)" items={f.hard} onChange={set('hard')} blank={empty} addLabel="Add sentence"
        render={(w, s) => <BilingualField label="Sentence" value={w} onChange={s} rows={2} />} />

      <ArrayField label="How we work (four short lines)" items={f.how} onChange={set('how')} blank={empty} addLabel="Add line"
        render={(w, s) => <BilingualField label="Line" value={w} onChange={s} rows={2} />} />

      <ArrayField label="Questions & answers (specific to this sector)" items={f.faq} onChange={set('faq')} blank={() => ({ q: empty(), a: empty() })} addLabel="Add question"
        render={(b, s) => (<>
          <BilingualField label="Question" value={b.q} onChange={(v) => s({ ...b, q: v })} rows={1} />
          <BilingualField label="Answer (40–60 words, answer-first)" value={b.a} onChange={(v) => s({ ...b, a: v })} rows={3} />
        </>)} />

      <button className="btn btn-success" disabled={saving} type="submit">{saving ? 'Saving…' : 'Save'}</button>
    </form>
  );
}
